import { memo, useCallback, useState } from 'react';
import { di } from 'react-magnetic-di';

import { pink115, spacer32, Type, white } from '@spotify-internal/encore-web';
import Image from 'next/image';
import PlanSvg from 'public/plan.svg';

import { SmallChip } from '@/components/SmallChip';
import { PLAN_CPM, PLAN_SUBSCRIPTION } from '@/data';

import {
  Hr,
  IconContainer,
  NoWrap,
  Total,
  TypeListItemStyled,
  TypeListStyled,
} from './PlanCard.styled';
import PlanCardItemPopover from './PlanCardItemPopover';

const triggerCss = {
  color: '#AFB1FF',
  whiteSpace: 'nowrap',
};

const PlanCardContents = memo(() => {
  di(
    Hr,
    IconContainer,
    Image,
    NoWrap,
    PlanCardItemPopover,
    SmallChip,
    Total,
    Type,
    TypeListItemStyled,
    TypeListStyled,
    useState,
  );

  const [activePopover, setActivePopover] = useState<'spotify' | undefined>();
  const onShowPodsights = useCallback(
    () => setActivePopover(activePopover === 'spotify' ? undefined : 'spotify'),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-creating callback breaks hover behavior
    [],
  );
  const onHide = useCallback(() => setActivePopover(undefined), []);

  return (
    <>
      <IconContainer>
        <Image
          alt="Plan Icon"
          src={PlanSvg}
          height="64"
          width="65.52"
          style={{ width: '65.52px', height: '64px' }}
        />
      </IconContainer>
      <Type as="h1" variant="canon" paddingBottom={spacer32}>
        With this plan, you get:
      </Type>
      <TypeListStyled>
        <TypeListItemStyled
          icon="spotify"
          isShown={activePopover === 'spotify'}
          variant="ballad"
          semanticColor="textSubdued"
        >
          Free{' '}
          <PlanCardItemPopover
            triggerText="Spotify Ad Analytics"
            isShown={activePopover === 'spotify'}
            onShow={onShowPodsights}
            onHide={onHide}
          >
            Spotify Ad Analytics helps you measure and drive advertising. With
            Megaphone, you can get access to Spotify Ad Analytics and your first
            4 million impressions free.{' '}
            <Type
              as="a"
              css={triggerCss}
              href="https://podsights.com/pricing"
              target="_blank"
            >
              Learn more ↗
            </Type>
          </PlanCardItemPopover>{' '}
          <NoWrap>
            <SmallChip backgroundColor={pink115} color={white}>
              New
            </SmallChip>
            <br /> with exclusive features for Megaphone users
          </NoWrap>
        </TypeListItemStyled>
        <TypeListItemStyled variant="ballad" semanticColor="textSubdued">
          Hosting & distribution capabilities
        </TypeListItemStyled>
        <TypeListItemStyled variant="ballad" semanticColor="textSubdued">
          Robust campaign management toolkit
        </TypeListItemStyled>
        <TypeListItemStyled variant="ballad" semanticColor="textSubdued">
          Detailed reporting & analytics
        </TypeListItemStyled>
        <TypeListItemStyled variant="ballad" semanticColor="textSubdued">
          Monetization tools via the Spotify Audience Network
        </TypeListItemStyled>
        <TypeListItemStyled variant="ballad" semanticColor="textSubdued">
          Live chat support, 4am-9pm M-F (ET)
        </TypeListItemStyled>
        <TypeListItemStyled variant="ballad" semanticColor="textSubdued">
          Unlimited users with role-based permissions
        </TypeListItemStyled>
        <TypeListItemStyled variant="ballad" semanticColor="textSubdued">
          {`156,250 monthly downloads & overage $${PLAN_CPM.toFixed(2)} CPM`}
        </TypeListItemStyled>
      </TypeListStyled>
      <Hr />
      <Total>
        <Type variant="canon">Total</Type>
        <div>
          <Type variant="canon">{`$${PLAN_SUBSCRIPTION} USD/month`}</Type>
          <Type as="p" variant="ballad" style={{ lineHeight: 1 }}>
            for 12 months
          </Type>
        </div>
      </Total>
    </>
  );
});

export default PlanCardContents;
